// Support Ticket popup — opens the CF7 modal from any "#support-ticket" trigger,
// paints custom dropdowns over the native selects, wires the drag & drop zone
// and swaps to the success panel once CF7 reports the mail was sent.

const OPEN_SELECTOR = 'a[href="#support-ticket"], a[href$="#support-ticket"], [data-support-open]';
const OPEN_LABEL = /^open\s+(a\s+)?support\s+ticket/i;
const CHEVRON =
  '<svg class="kd-select__chevron" viewBox="0 0 20 20" aria-hidden="true" focusable="false">' +
  '<path d="M5 8l5 5 5-5" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';

let popup = null;
let lastFocused = null;

// Modal open / close
function setPanel(name) {
  popup.querySelectorAll('[data-support-panel]').forEach((panel) => {
    panel.hidden = panel.dataset.supportPanel !== name;
  });
}

function openPopup() {
  if (!popup || popup.classList.contains('is-open')) return;

  lastFocused = document.activeElement;
  setPanel('form');
  popup.hidden = false;
  document.body.style.overflow = 'hidden';
  if (window.__kdLenis) window.__kdLenis.stop();

  // Flush the `hidden` removal so the opening transition actually runs.
  void popup.offsetWidth;
  popup.classList.add('is-open');

  const first = popup.querySelector('[data-support-panel]:not([hidden]) input, [data-support-panel]:not([hidden]) button');
  if (first) first.focus({ preventScroll: true });
}

function closePopup() {
  if (!popup || !popup.classList.contains('is-open')) return;

  popup.classList.remove('is-open');
  document.body.style.overflow = '';
  if (window.__kdLenis) window.__kdLenis.start();

  window.setTimeout(() => {
    popup.hidden = true;
    setPanel('form');
  }, 300);

  if (lastFocused && typeof lastFocused.focus === 'function') {
    lastFocused.focus({ preventScroll: true });
  }
}

// Keep Tab inside the visible panel.
function trapFocus(event) {
  if (event.key !== 'Tab') return;

  const panel = popup.querySelector('[data-support-panel]:not([hidden])');
  if (!panel) return;

  const items = [...panel.querySelectorAll('a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"])')].filter(
    (el) => !el.disabled && el.offsetParent !== null
  );
  if (!items.length) return;

  const first = items[0];
  const last = items[items.length - 1];

  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
}

// Custom dropdowns
function closeAllSelects(except) {
  popup.querySelectorAll('.kd-select.is-open').forEach((el) => {
    if (el !== except) el.classList.remove('is-open');
  });
}

function buildSelect(select) {
  const wrap = document.createElement('div');
  wrap.className = 'kd-select';
  select.parentNode.insertBefore(wrap, select);
  wrap.appendChild(select);

  const toggle = document.createElement('button');
  toggle.type = 'button';
  toggle.className = 'kd-select__toggle';
  toggle.setAttribute('aria-haspopup', 'listbox');
  toggle.setAttribute('aria-expanded', 'false');
  toggle.innerHTML = '<span class="kd-select__value"></span>' + CHEVRON;

  const panel = document.createElement('ul');
  panel.className = 'kd-select__panel';
  panel.setAttribute('role', 'listbox');

  const label = select.closest('.input-col')?.querySelector('.form-label');
  if (label) toggle.setAttribute('aria-label', label.textContent.trim());

  const value = toggle.querySelector('.kd-select__value');
  const options = [...select.options];

  const sync = () => {
    value.textContent = select.options[select.selectedIndex]?.text || '';
    panel.querySelectorAll('.kd-select__option').forEach((li, i) => {
      li.classList.toggle('is-selected', i === select.selectedIndex);
      li.setAttribute('aria-selected', i === select.selectedIndex ? 'true' : 'false');
    });
  };

  options.forEach((option, index) => {
    const item = document.createElement('li');
    item.className = 'kd-select__option';
    item.setAttribute('role', 'option');
    item.textContent = option.text;
    item.addEventListener('click', () => {
      select.selectedIndex = index;
      select.dispatchEvent(new Event('change', { bubbles: true }));
      sync();
      wrap.classList.remove('is-open');
      toggle.setAttribute('aria-expanded', 'false');
      toggle.focus();
    });
    panel.appendChild(item);
  });

  toggle.addEventListener('click', () => {
    const willOpen = !wrap.classList.contains('is-open');
    closeAllSelects(wrap);
    wrap.classList.toggle('is-open', willOpen);
    toggle.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
  });

  toggle.addEventListener('keydown', (event) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const step = event.key === 'ArrowDown' ? 1 : -1;
      const next = Math.min(options.length - 1, Math.max(0, select.selectedIndex + step));
      select.selectedIndex = next;
      select.dispatchEvent(new Event('change', { bubbles: true }));
      sync();
    } else if (event.key === 'Escape') {
      wrap.classList.remove('is-open');
      toggle.setAttribute('aria-expanded', 'false');
    }
  });

  wrap.appendChild(toggle);
  wrap.appendChild(panel);
  select.addEventListener('change', sync);
  sync();
}

// Drag & drop zone
function formatSize(bytes) {
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

function buildDropzone(zone) {
  const input = zone.querySelector('input[type="file"]');
  const output = zone.querySelector('[data-dropzone-file]');
  if (!input) return;

  const render = () => {
    if (!output) return;
    output.textContent = input.files.length
      ? input.files[0].name + ' (' + formatSize(input.files[0].size) + ')'
      : '';
  };

  zone.addEventListener('click', (event) => {
    if (event.target !== input) input.click();
  });

  ['dragenter', 'dragover'].forEach((name) =>
    zone.addEventListener(name, (event) => {
      event.preventDefault();
      zone.classList.add('is-dragover');
    })
  );

  ['dragleave', 'drop'].forEach((name) =>
    zone.addEventListener(name, (event) => {
      event.preventDefault();
      zone.classList.remove('is-dragover');
    })
  );

  zone.addEventListener('drop', (event) => {
    const files = event.dataTransfer?.files;
    if (!files || !files.length) return;

    const transfer = new DataTransfer();
    transfer.items.add(files[0]);
    input.files = transfer.files;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  input.addEventListener('change', render);
  render();
}

export default function initSupportPopup() {
  popup = document.getElementById('kd-support-popup');
  if (!popup) return;

  // Match the anchor first, then fall back to the button's own label so the CTA
  // still opens the popup wherever it is placed (menu, pattern, template part).
  document.addEventListener('click', (event) => {
    const control = event.target.closest('a, button');
    if (!control || popup.contains(control)) return;

    const trigger =
      control.matches(OPEN_SELECTOR) || OPEN_LABEL.test(control.textContent.trim());
    if (!trigger) return;

    event.preventDefault();
    openPopup();
  });

  popup.addEventListener('click', (event) => {
    if (event.target.closest('[data-support-close]')) {
      event.preventDefault();
      closePopup();
      return;
    }
    if (!event.target.closest('.kd-select')) closeAllSelects();
  });

  document.addEventListener('keydown', (event) => {
    if (!popup.classList.contains('is-open')) return;
    if (event.key === 'Escape') {
      closeAllSelects();
      closePopup();
      return;
    }
    trapFocus(event);
  });

  popup.querySelectorAll('.support-ticket-form select').forEach(buildSelect);
  popup.querySelectorAll('[data-dropzone]').forEach(buildDropzone);

  // Textarea grows with its content instead of scrolling inside 36px.
  popup.querySelectorAll('.support-ticket-form textarea').forEach((area) => {
    const grow = () => {
      area.style.height = 'auto';
      area.style.height = Math.min(area.scrollHeight, 160) + 'px';
    };
    area.addEventListener('input', grow);
  });

  // CF7 success -> success panel.
  popup.querySelectorAll('form.wpcf7-form').forEach((form) => {
    form.addEventListener('wpcf7mailsent', () => {
      setPanel('thanks');
      const close = popup.querySelector('[data-support-panel="thanks"] [data-support-close]');
      if (close) close.focus({ preventScroll: true });
    });
  });
}
